/**
 * AWS Amplify Authentication Service
 */

import { signIn, signUp, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword, updatePassword, getCurrentUser, signOut, fetchAuthSession, fetchUserAttributes, confirmSignIn } from 'aws-amplify/auth';
import { CognitoIdentityProviderClient } from '@aws-sdk/client-cognito-identity-provider';
import { Hub } from 'aws-amplify/utils';
import { configureAmplify } from './amplifyConfig';
import {
    type User,
    type AuthTokens,
    type AuthSession,
    type LoginCredentials,
    type SignUpCredentials,
    type ResetPasswordRequest,
    type ConfirmPasswordReset,
    type AuthError,
} from './types';

// Configure Amplify on module load
configureAmplify();

export class AmplifyAuthService {
    private static instance: AmplifyAuthService;
    private session: AuthSession | null = null;
    private sessionPromise: Promise<AuthSession> | null = null;
    private authStateListeners: ((session: AuthSession | null) => void)[] = [];
    private pendingChallenge: { challengeName: string; challengeParameters: any } | null = null;

    private constructor() {
        // Listen to auth state changes
        Hub.listen('auth', (data) => {
            const { payload } = data;
            switch (payload.event) {
                case 'signedIn':
                    this.handleSignedIn();
                    break;
                case 'signedOut':
                    this.handleSignedOut();
                    break;
                case 'tokenRefresh':
                    this.handleTokenRefresh();
                    break;
                case 'tokenRefresh_failure':
                    this.handleTokenRefreshFailure();
                    break;
            }
        });
    }

    public static getInstance(): AmplifyAuthService {
        if (!AmplifyAuthService.instance) {
            AmplifyAuthService.instance = new AmplifyAuthService();
        }
        return AmplifyAuthService.instance;
    }

    /**
     * Add auth state listener
     */
    addAuthStateListener(listener: (session: AuthSession | null) => void): () => void {
        this.authStateListeners.push(listener);
        return () => {
            const index = this.authStateListeners.indexOf(listener);
            if (index > -1) {
                this.authStateListeners.splice(index, 1);
            }
        };
    }

    /**
     * Notify auth state listeners
     */
    private notifyAuthStateListeners(session: AuthSession | null): void {
        this.authStateListeners.forEach(listener => listener(session));
    }

    /**
     * Sign in with username and password
     */
    async signIn(credentials: LoginCredentials): Promise<AuthSession> {
        try {
            const result = await signIn({
                username: credentials.username,
                password: credentials.password,
            });

            if (result.isSignedIn) {
                return await this.getCurrentSession();
            } else if (result.nextStep) {
                // Store challenge information for handling
                console.log('Next step details:', result.nextStep);
                this.pendingChallenge = {
                    challengeName: result.nextStep.signInStep,
                    challengeParameters: {
                        ...result.nextStep
                    },
                };
                throw new Error(`Authentication challenge required: ${result.nextStep.signInStep}`);
            } else {
                throw new Error('Sign in failed');
            }
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Sign up new user
     */
    async signUp(credentials: SignUpCredentials): Promise<{ userId: string; needsConfirmation: boolean }> {
        try {
            const result = await signUp({
                username: credentials.username,
                password: credentials.password,
                options: {
                    userAttributes: {
                        email: credentials.email,
                        ...(credentials.name && { name: credentials.name }),
                        ...(credentials.phoneNumber && { phone_number: credentials.phoneNumber }),
                    },
                },
            });

            return {
                userId: result.userId || '',
                needsConfirmation: result.isSignUpComplete === false,
            };
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Confirm sign up with verification code
     */
    async confirmSignUp(username: string, code: string): Promise<void> {
        try {
            await confirmSignUp({
                username,
                confirmationCode: code,
            });
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Resend confirmation code
     */
    async resendConfirmationCode(username: string): Promise<void> {
        try {
            await resendSignUpCode({ username });
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Initiate forgot password flow
     */
    async forgotPassword(request: ResetPasswordRequest): Promise<void> {
        try {
            const username = request.username?.trim();
            if (!username) {
                throw new Error('Username is required to reset password');
            }
            await resetPassword({ username });
        } catch (error) {
            console.error('Forgot password error:', error);
            throw this.handleAuthError(error);
        }
    }

    /**
     * Confirm password reset
     */
    async confirmForgotPassword(reset: ConfirmPasswordReset): Promise<void> {
        try {
            await confirmResetPassword({
                username: reset.username,
                confirmationCode: reset.code,
                newPassword: reset.newPassword,
            });
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Change password for authenticated user
     */
    async changePassword(oldPassword: string, newPassword: string): Promise<void> {
        try {
            await updatePassword({
                oldPassword,
                newPassword,
            });
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Get current user information
     */
    async getCurrentUser(): Promise<User> {
        try {
            const user = await getCurrentUser();
            const attributes = await fetchUserAttributes();

            return {
                id: user.userId,
                email: attributes.email || '',
                username: user.username,
                name: attributes.name || attributes.given_name || '',
                picture: attributes.picture || '',
                emailVerified: Boolean(attributes.email_verified),
                phoneNumber: attributes.phone_number || '',
                phoneNumberVerified: Boolean(attributes.phone_number_verified),
                attributes: attributes as Record<string, string>,
            };
        } catch (error) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Get current session
     */
    async getCurrentSession(): Promise<AuthSession> {
        // Return cached session if valid (with a small buffer)
        if (this.isSessionValid()) {
            return this.session as AuthSession;
        }

        // Coalesce concurrent refreshes
        if (this.sessionPromise) {
            return this.sessionPromise;
        }

        this.sessionPromise = (async () => {
            try {
                const session = await fetchAuthSession();
                const user = await this.getCurrentUser();

                if (!session.tokens) {
                    throw new Error('No tokens in session');
                }

                const tokens: AuthTokens = {
                    accessToken: session.tokens.accessToken?.toString() || '',
                    idToken: session.tokens.idToken?.toString() || '',
                    refreshToken: '', // Amplify handles refresh tokens internally
                    expiresIn: session.tokens.accessToken?.payload?.exp ?
                        session.tokens.accessToken.payload.exp - Math.floor(Date.now() / 1000) : 3600,
                    tokenType: 'Bearer',
                };

                const authSession: AuthSession = {
                    user,
                    tokens,
                    isAuthenticated: true,
                    expiresAt: session.tokens.accessToken?.payload?.exp ?
                        session.tokens.accessToken.payload.exp * 1000 : Date.now() + (tokens.expiresIn * 1000),
                };

                this.session = authSession;
                return authSession;
            } catch (error) {
                throw this.handleAuthError(error);
            } finally {
                this.sessionPromise = null;
            }
        })();

        return this.sessionPromise;
    }

    /**
     * Sign out user
     */
    async signOut(): Promise<void> {
        try {
            await signOut();
        } catch (error) {
            console.warn('Error during sign out:', error);
        } finally {
            this.session = null;
            this.pendingChallenge = null;
            this.notifyAuthStateListeners(null);
        }
    }

    /**
     * Check if user is authenticated
     */
    async isAuthenticated(): Promise<boolean> {
        if (this.isSessionValid()) return true;
        try {
            await getCurrentUser();
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Respond to new password challenge
     */
    async respondToNewPasswordChallenge(newPassword: string, userAttributes?: Record<string, string>): Promise<AuthSession> {
        try {
            if (!this.pendingChallenge || this.pendingChallenge.challengeName !== 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                throw new Error('No new password challenge pending');
            }

            // Log the pending challenge for debugging
            console.log('Pending challenge:', this.pendingChallenge);
            console.log('Challenge parameters:', this.pendingChallenge.challengeParameters);

            // For CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED challenge, we need to provide the new password
            // and any required user attributes. Since the Cognito User Pool requires the 'name' attribute,
            // the UI now requires users to provide their name.

            // Since the UI now requires the name field, userAttributes should always contain the name
            if (!userAttributes || !userAttributes.name) {
                throw new Error('Name is required but not provided');
            }

            const finalUserAttributes = userAttributes;

            // Try using Amplify's confirmSignIn with user attributes
            const result = await confirmSignIn({
                challengeResponse: newPassword,
                options: {
                    userAttributes: finalUserAttributes
                }
            });

            if (result.isSignedIn) {
                this.pendingChallenge = null;
                return await this.getCurrentSession();
            } else {
                throw new Error('Failed to confirm new password');
            }
        } catch (error) {
            // Log the full error for debugging
            console.error('New password challenge error:', error);

            // Handle specific error for missing name attribute during new password challenge
            if (error instanceof Error && error.message.includes('name is missing')) {
                throw new Error('Unable to set new password due to incomplete user profile. The name attribute is required but missing. Please contact your administrator to complete your profile setup.');
            }
            throw this.handleAuthError(error);
        }
    }

    /**
     * Get pending challenge
     */
    getPendingChallenge(): { challengeName: string; challengeParameters: any } | null {
        return this.pendingChallenge;
    }

    /**
     * Clear pending challenge
     */
    clearPendingChallenge(): void {
        this.pendingChallenge = null;
    }

    /**
     * Get stored session (for compatibility)
     */
    getStoredSession(): AuthSession | null {
        return this.session;
    }

    /**
     * Handle signed in event
     */
    private async handleSignedIn(): Promise<void> {
        try {
            const session = await this.getCurrentSession();
            this.notifyAuthStateListeners(session);
        } catch (error) {
            console.error('Error handling signed in event:', error);
        }
    }

    /**
     * Handle signed out event
     */
    private handleSignedOut(): void {
        this.session = null;
        this.notifyAuthStateListeners(null);
    }

    /**
     * Handle token refresh
     */
    private async handleTokenRefresh(): Promise<void> {
        try {
            const session = await this.getCurrentSession();
            this.notifyAuthStateListeners(session);
        } catch (error) {
            console.error('Error handling token refresh:', error);
        }
    }

    /**
     * Handle token refresh failure
     */
    private handleTokenRefreshFailure(): void {
        this.session = null;
        this.notifyAuthStateListeners(null);
    }

    /**
     * Helpers
     */
    private isSessionValid(bufferSeconds: number = 60): boolean {
        if (!this.session) return false;
        const now = Date.now();
        const expiresAt = this.session.expiresAt;
        return typeof expiresAt === 'number' && (expiresAt - now > bufferSeconds * 1000);
    }

    public async getAuthHeader(): Promise<Record<string, string>> {
        const session = this.isSessionValid() ? this.session! : await this.getCurrentSession();
        if (!session.tokens.idToken) throw { name: 'Unauthorized', message: 'Missing id token' };
        return { Authorization: `Bearer ${session.tokens.idToken}` };
    }

    /**
     * Handle authentication errors
     */
    private handleAuthError(error: any): AuthError {
        const authError: AuthError = {
            code: error.name || 'UnknownError',
            message: error.message || 'An unknown error occurred',
            name: error.name || 'UnknownError',
        };

        // Map common Amplify errors to user-friendly messages
        switch (error.name) {
            case 'NotAuthorizedException':
                authError.message = 'Invalid username or password';
                break;
            case 'UserNotFoundException':
                authError.message = 'User not found';
                break;
            case 'UserNotConfirmedException':
                authError.message = 'Please confirm your email address';
                break;
            case 'InvalidPasswordException':
                authError.message = 'Password does not meet requirements';
                break;
            case 'UsernameExistsException':
                authError.message = 'Username already exists';
                break;
            case 'InvalidParameterException':
                authError.message = 'Invalid input parameters';
                break;
            case 'TooManyRequestsException':
                authError.message = 'Too many requests. Please try again later';
                break;
            case 'ExpiredCodeException':
                authError.message = 'Verification code has expired';
                break;
            case 'CodeMismatchException':
                authError.message = 'Invalid verification code';
                break;
            case 'LimitExceededException':
                authError.message = 'Attempt limit exceeded. Please try again later';
                break;
            case 'InvalidUserPoolConfigurationException':
                authError.message = 'Invalid user pool configuration';
                break;
        }

        if (typeof error?.message === 'string') {
            const lowerMessage = error.message.toLowerCase();
            if (lowerMessage.includes('no registered/verified email') || lowerMessage.includes('no registered/verified phone')) {
                authError.message = 'We cannot reset the password because this account has no verified email or phone number. Please contact your administrator to update the account.';
            }
        }

        return authError;
    }
}

// Export singleton instance
export const amplifyAuthService = AmplifyAuthService.getInstance();
